import { useState } from "react";
import { EditableField } from "src/components/EditableField/EditableField";
import { Color, Flex, Text } from "src/elements";
import { translate } from "src/i18n/translate";
import { displayLength } from "src/utils/lengthUnit";

export const parseFrontOverhang = (text: string): number | null => {
  if (!/^\d+(?:\.\d{1,2})?$/.test(text.trim())) return null;
  const value = Math.round(Number(text) * 100);
  return Number.isSafeInteger(value) && value <= 2147483647 ? value : null;
};

export const FrontOverhangField = ({
  value,
  eligible,
  onSave,
}: {
  value: number;
  eligible: boolean;
  onSave?: (value: number) => Promise<string | null>;
}) => {
  const [error, setError] = useState<string | null>(null);
  const save = async (text: string) => {
    const allowance = parseFrontOverhang(text);
    const failed =
      allowance === null
        ? translate("common", "frontOverhang.fixtures.invalidLength")
        : allowance > 0 && !eligible
          ? translate("common", "frontOverhang.fixtures.ineligible")
          : ((await onSave?.(allowance)) ?? null);
    setError(failed);
    if (failed || allowance === null) throw new Error(failed ?? "Invalid allowance");
    return String(displayLength(allowance));
  };
  return (
    <Flex column gap="2px" title={translate("common", "frontOverhang.fixtures.help")}>
      <EditableField<string>
        value={String(displayLength(value))}
        onSave={save}
        isSavedValueShown
        inputProps={{
          "aria-label": translate("common", "frontOverhang.fixtures.header"),
          disabled: !onSave || (!eligible && value === 0),
          size: 6,
        }}
      />
      {error && (
        <Text color={Color.red}>
          <span role="alert">{error}</span>
        </Text>
      )}
    </Flex>
  );
};
